import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { SeverityBadge, StatusBadge } from "@/components/Primitives";
import { Search, Plus, X } from "lucide-react";
import { toast } from "sonner";

export default function Tickets() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [showNew, setShowNew] = useState(false);

  const [form, setForm] = useState({
    client_id: "",
    asset_id: "",
    title: "",
    description: "",
    category: "general",
    priority: "medium",
    assignee_id: "",
  });

  const load = async () => {
    const [ticketsRes, clientsRes, usersRes, assetsRes] = await Promise.allSettled([
      api.get("/tickets", { params: { limit: 500 } }),
      api.get("/clients"),
      api.get("/users"),
      api.get("/assets", { params: { limit: 1000 } }),
    ]);

    if (ticketsRes.status === "fulfilled") setItems(ticketsRes.value.data);
    if (clientsRes.status === "fulfilled") setClients(clientsRes.value.data);
    if (usersRes.status === "fulfilled") setUsers(usersRes.value.data);
    if (assetsRes.status === "fulfilled") setAssets(assetsRes.value.data);
  };

  useEffect(() => { load(); }, []);

  const userMap = useMemo(() => Object.fromEntries(users.map(u => [u.id, u])), [users]);
  const clientMap = useMemo(() => Object.fromEntries(clients.map(c => [c.id, c])), [clients]);

  const technicians = users.filter(u =>
    ["msp_owner", "service_manager", "service_desk", "technician", "admin"].includes(u.role)
  );

  const filtered = items
    .filter(t => !q || `${t.title || ""} ${t.category || ""} ${t.number || ""} ${clientMap[t.client_id]?.name || ""}`.toLowerCase().includes(q.toLowerCase()))
    .filter(t => !status || t.status === status)
    .filter(t => !priority || t.priority === priority);

  const createTicket = async () => {
    try {
      if (!form.title.trim()) return toast.error("Ticket title is required");
      if (!form.description.trim()) return toast.error("Description is required");

      const payload = Object.fromEntries(Object.entries(form).filter(([_, v]) => v !== ""));
      const { data } = await api.post("/tickets", payload);

      toast.success(`Ticket #${data.number} created`);
      setShowNew(false);
      setForm({
        client_id: "",
        asset_id: "",
        title: "",
        description: "",
        category: "general",
        priority: "medium",
        assignee_id: "",
      });
      load();
    } catch (err) {
      console.error("Create ticket failed:", err);
      toast.error(err?.response?.data?.detail || err?.message || "Create ticket failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Service Desk · PSA</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Tickets ({filtered.length})</h1>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowNew(!showNew)}
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-2 text-sm flex items-center gap-2"
          >
            {showNew ? <X size={15} /> : <Plus size={15} />}
            {showNew ? "Close" : "New Ticket"}
          </button>

          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search tickets" className="bg-zinc-950 border border-zinc-800 rounded-md pl-9 pr-3 py-2 text-sm w-72 focus:outline-none focus:border-blue-500/60" />
          </div>

          <select value={status} onChange={e=>setStatus(e.target.value)} className="bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm">
            <option value="">All status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="pending">Pending</option>
            <option value="closed">Closed</option>
          </select>

          <select value={priority} onChange={e=>setPriority(e.target.value)} className="bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm">
            <option value="">All priority</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
      </div>

      {showNew && (
        <div className="bg-zinc-950 border border-blue-900/50 rounded-md p-5 shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-display text-lg font-semibold">Create New Ticket</div>
              <div className="text-xs text-zinc-500 mt-1">Create, assign, and classify a service desk request.</div>
            </div>
            <button onClick={() => setShowNew(false)} className="text-zinc-500 hover:text-zinc-100"><X size={18}/></button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <Field label="Client">
              <select value={form.client_id} onChange={e=>setForm({...form, client_id:e.target.value})} className="input">
                <option value="">No client selected</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>

            <Field label="Asset">
              <select value={form.asset_id} onChange={e=>setForm({...form, asset_id:e.target.value})} className="input">
                <option value="">No asset linked</option>
                {assets.map(a => <option key={a.id} value={a.id}>{a.hostname || a.name || a.id}</option>)}
              </select>
            </Field>

            <Field label="Assigned Technician">
              <select value={form.assignee_id} onChange={e=>setForm({...form, assignee_id:e.target.value})} className="input">
                <option value="">Unassigned</option>
                {technicians.map(u => <option key={u.id} value={u.id}>{u.name || u.email} · {u.role}</option>)}
              </select>
            </Field>

            <Field label="Category">
              <select value={form.category} onChange={e=>setForm({...form, category:e.target.value})} className="input">
                {["general","hardware","software","network","printer","security","account access","backup","performance"].map(x => <option key={x} value={x}>{x}</option>)}
              </select>
            </Field>

            <Field label="Priority">
              <select value={form.priority} onChange={e=>setForm({...form, priority:e.target.value})} className="input">
                {["low","medium","high","critical"].map(x => <option key={x} value={x}>{x}</option>)}
              </select>
            </Field>

            <Field label="Title">
              <input value={form.title} onChange={e=>setForm({...form, title:e.target.value})} className="input" placeholder="Cannot print to network printer" />
            </Field>

            <div className="md:col-span-2 xl:col-span-3">
              <Field label="Description">
                <textarea value={form.description} onChange={e=>setForm({...form, description:e.target.value})} rows={4} className="input" placeholder="Describe the issue, affected user, urgency, and troubleshooting already done." />
              </Field>
            </div>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setShowNew(false)} className="bg-zinc-900 border border-zinc-800 rounded px-4 py-2 text-sm">Cancel</button>
            <button onClick={createTicket} className="bg-blue-600 hover:bg-blue-700 rounded px-4 py-2 text-sm text-white">Create Ticket</button>
          </div>
        </div>
      )}

      <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/50 border-b border-zinc-900 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
            <tr>
              <th className="text-left px-4 py-3 w-20">#</th>
              <th className="text-left px-4 py-3">Title</th>
              <th className="text-left px-4 py-3">Client</th>
              <th className="text-left px-4 py-3">Assignee</th>
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-left px-4 py-3">Priority</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">SLA</th>
              <th className="text-left px-4 py-3">Created</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 200).map(t => (
              <tr key={t.id} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                <td className="px-4 py-2 font-mono text-zinc-500 text-xs">#{t.number}</td>
                <td className="px-4 py-2"><Link to={`/tickets/${t.id}`} className="hover:text-blue-400">{t.title}</Link></td>
                <td className="px-4 py-2 text-zinc-400">{clientMap[t.client_id]?.name || t.client_id || "—"}</td>
                <td className="px-4 py-2 text-zinc-400">{userMap[t.assignee_id]?.name || userMap[t.assignee_id]?.email || "Unassigned"}</td>
                <td className="px-4 py-2 text-zinc-400">{t.category}</td>
                <td className="px-4 py-2"><SeverityBadge level={t.priority} /></td>
                <td className="px-4 py-2"><StatusBadge status={t.status} /></td>
                <td className="px-4 py-2 font-mono text-[11px] text-zinc-500">{t.sla_deadline?.slice(0,10)}</td>
                <td className="px-4 py-2 font-mono text-[11px] text-zinc-500">{t.created_at?.slice(0,10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <style>{`
        .input {
          width: 100%;
          background: rgb(24 24 27);
          border: 1px solid rgb(39 39 42);
          border-radius: 0.375rem;
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          outline: none;
        }
        .input:focus {
          border-color: rgba(59, 130, 246, 0.7);
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">{label}</div>
      {children}
    </label>
  );
}
